import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { dt } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { ProductPicker } from "@/components/ProductPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReceiptText, Upload, Check, X, Loader2, Plus, Camera, ScanSearch, PackageCheck } from "lucide-react";
import { toast } from "sonner";

function newRow() {
  return { texto_detectado: "(agregado a mano)", product_id: "", mode: "existente", nombre_nuevo: "", categoria_nueva: "", marca_nueva: "", precio_nuevo: 0, cantidad: 1, costo_unitario: 0 };
}

export default function InvoiceUpload() {
  const [invoices, setInvoices] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [current, setCurrent] = useState(null); // invoice being reviewed
  const [rows, setRows] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const loadInv = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { loadInv(); }, []);

  const startFromInvoice = (inv) => {
    setCurrent(inv);
    setRows(inv.items_extraidos.map((it) => ({
      ...it,
      product_id: it.product_id_sugerido || "",
      mode: it.es_producto_nuevo ? "nuevo" : "existente",
      nombre_nuevo: it.texto_detectado,
      categoria_nueva: "",
      marca_nueva: "",
      precio_nuevo: 0,
    })));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.paginas_totales > 1) toast.info(`Procesamos la página 1 de ${data.paginas_totales}`);
      if (!data.items_extraidos || data.items_extraidos.length === 0) {
        toast.error("No pudimos leer esta factura. Podés cargar los productos a mano con «Agregar renglón», o probar con una foto más nítida y derecha.");
      } else {
        toast.success("Factura analizada por la IA");
      }
      await loadInv();
      startFromInvoice(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo analizar la factura");
    } finally { setAnalyzing(false); e.target.value = ""; }
  };

  const updateRow = (i, patch) => setRows((prev) => prev.map((x, j) => j === i ? { ...x, ...patch } : x));
  const addRow = () => setRows((prev) => [...prev, newRow()]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, j) => j !== i));

  const rowsSinProducto = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.mode === "existente" && !r.product_id);

  const confirm = async () => {
    if (rows.length === 0) return toast.error("Todavía no hay renglones. Agregá al menos uno o subí otra foto.");
    if (rowsSinProducto.length > 0) {
      return toast.error(`Hay ${rowsSinProducto.length} renglón(es) sin producto asignado. Elegí el producto o eliminá esos renglones.`);
    }
    setConfirming(true);
    try {
      const payload = {
        items: rows.map((r) => ({
          product_id: r.mode === "existente" ? r.product_id : null,
          es_producto_nuevo: r.mode === "nuevo",
          nombre_nuevo: r.mode === "nuevo" ? r.nombre_nuevo : null,
          categoria_nueva: r.mode === "nuevo" ? r.categoria_nueva : null,
          marca_nueva: r.mode === "nuevo" ? r.marca_nueva : null,
          precio_nuevo: r.mode === "nuevo" ? Number(r.precio_nuevo) : null,
          cantidad: Number(r.cantidad), costo_unitario: Number(r.costo_unitario),
        })),
      };
      await api.post(`/invoices/${current.id}/confirm`, payload);
      toast.success("Stock actualizado con la factura ✨");
      setCurrent(null); loadInv();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al confirmar"); }
    finally { setConfirming(false); }
  };

  const discard = async (inv) => { await api.post(`/invoices/${inv.id}/discard`); loadInv(); if (current?.id === inv.id) setCurrent(null); };

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto">
      <PageHeader title="Cargar factura" subtitle="Subí la foto de la factura del proveedor y la IA extrae los productos.">
        <label>
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="invoice-file" />
          <Button asChild className="rounded-full bg-primary hover:bg-primary/90 gap-2" disabled={analyzing}>
            <span>{analyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analizando…</> : <><Upload className="h-4 w-4" /> Subir factura</>}</span>
          </Button>
        </label>
      </PageHeader>

      {!current && (
        <div className="ms-card p-5 mb-6" data-testid="invoice-instructions">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[[Camera, "Sacale una foto a la factura del proveedor (o subí el PDF)."],
              [ScanSearch, "Revisá los renglones que detectamos y corregí lo que haga falta."],
              [PackageCheck, "Confirmá: recién ahí se suma el stock."]].map(([Icon, txt], i) => (
              <div key={i} className="flex gap-3">
                <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">{i + 1}</span>
                <div>
                  <Icon className="h-4 w-4 text-primary mb-1" />
                  <p className="text-sm text-muted-foreground">{txt}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">El stock no se toca hasta que confirmes.</p>
        </div>
      )}

      {current && (
        <div className="ms-card p-5 mb-6" data-testid="invoice-review">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-64 shrink-0">
              <img src={fileUrl(current.foto_url)} alt="factura" className="w-full rounded-2xl border border-border object-cover" />
              <p className="text-xs text-muted-foreground mt-2">Respaldo de la factura{current.paginas_totales > 1 ? ` (página 1 de ${current.paginas_totales})` : ""}</p>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-head font-semibold mb-1">Revisá y confirmá los renglones</h3>
              <p className="text-sm text-muted-foreground mb-4">El stock no se toca hasta que confirmes.</p>
              <div className="space-y-3">
                {rows.map((r, i) => {
                  const faltaProducto = r.mode === "existente" && !r.product_id;
                  return (
                  <div key={i} className={`rounded-2xl border p-3 space-y-2 ${faltaProducto ? "border-amber-300 bg-amber-50/40" : "border-border"}`} data-testid={`invoice-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Detectado: "{r.texto_detectado}"</p>
                      <button onClick={() => removeRow(i)} className="text-red-400 hover:text-red-600" data-testid={`invrow-remove-${i}`}><X className="h-4 w-4" /></button>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <Select value={r.mode} onValueChange={(v) => updateRow(i, { mode: v })}>
                        <SelectTrigger className="w-44 rounded-xl" data-testid={`invrow-mode-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="existente">Producto existente</SelectItem>
                          <SelectItem value="nuevo">Crear producto nuevo</SelectItem>
                        </SelectContent>
                      </Select>
                      {r.mode === "existente" ? (
                        <div className="flex-1 min-w-[200px]">
                          <ProductPicker value={r.product_id || null} onChange={(pid) => updateRow(i, { product_id: pid })} placeholder="Buscar producto…" testid={`invrow-product-${i}`} />
                        </div>
                      ) : (
                        <Input placeholder="Nombre nuevo" value={r.nombre_nuevo} onChange={(e) => updateRow(i, { nombre_nuevo: e.target.value })} className="flex-1 min-w-[180px] rounded-xl" data-testid={`invrow-nombre-nuevo-${i}`} />
                      )}
                    </div>
                    {faltaProducto && <p className="text-xs text-amber-600">Elegí un producto para este renglón.</p>}
                    <div className="flex gap-2 flex-wrap">
                      <div className="flex-1 min-w-[90px]"><label className="text-xs text-muted-foreground">Cantidad</label><Input type="number" value={r.cantidad} onChange={(e) => updateRow(i, { cantidad: e.target.value })} className="rounded-xl" data-testid={`invrow-qty-${i}`} /></div>
                      <div className="flex-1 min-w-[90px]"><label className="text-xs text-muted-foreground">Costo unit.</label><Input type="number" value={r.costo_unitario} onChange={(e) => updateRow(i, { costo_unitario: e.target.value })} className="rounded-xl" /></div>
                      {r.mode === "nuevo" && <>
                        <div className="flex-1 min-w-[90px]"><label className="text-xs text-muted-foreground">Precio venta</label><Input type="number" value={r.precio_nuevo} onChange={(e) => updateRow(i, { precio_nuevo: e.target.value })} className="rounded-xl" data-testid={`invrow-precio-${i}`} /></div>
                        <div className="flex-1 min-w-[90px]"><label className="text-xs text-muted-foreground">Marca</label><Input value={r.marca_nueva} onChange={(e) => updateRow(i, { marca_nueva: e.target.value })} className="rounded-xl" data-testid={`invrow-marca-${i}`} /></div>
                      </>}
                    </div>
                  </div>
                  );
                })}
                {rows.length === 0 && <p className="text-sm text-muted-foreground">No pudimos leer esta factura. Podés cargar los productos a mano con «Agregar renglón», o probar con una foto más nítida y derecha.</p>}
                <Button variant="outline" className="rounded-full gap-2 w-full" onClick={addRow} data-testid="invoice-add-row"><Plus className="h-4 w-4" /> Agregar renglón</Button>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" className="rounded-full" onClick={() => setCurrent(null)}>Cerrar</Button>
                <Button onClick={confirm} disabled={confirming} data-testid="invoice-confirm" className="rounded-full bg-primary hover:bg-primary/90 gap-2"><Check className="h-4 w-4" /> {confirming ? "Confirmando…" : "Confirmar y sumar stock"}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className="font-head text-lg font-semibold mb-3">Facturas cargadas</h2>
      {!invoices ? <Loader /> : invoices.length === 0 ? (
        <EmptyState icon={ReceiptText} title="Sin facturas todavía" subtitle="Cuando subas una factura de proveedor aparecerá acá." />
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.id} className="ms-card p-3 flex items-center gap-3" data-testid={`invoice-${inv.id}`}>
              <img src={fileUrl(inv.foto_url)} alt="" className="h-14 w-14 rounded-xl object-cover border border-border" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{inv.items_extraidos?.length || 0} renglones · {dt(inv.fecha)}</p>
                <Badge className={`rounded-full border-0 mt-1 ${inv.estado === "confirmada" ? "bg-emerald-50 text-emerald-600" : inv.estado === "descartada" ? "bg-gray-100 text-gray-500" : "bg-amber-50 text-amber-600"}`}>
                  {inv.estado.replace("_", " ")}
                </Badge>
              </div>
              {inv.estado === "pendiente_confirmacion" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => startFromInvoice(inv)} data-testid={`invoice-review-${inv.id}`}>Revisar</Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => discard(inv)}><X className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
